import { createServerSideHelpers } from "@trpc/react-query/server";
import type { GetStaticPaths, GetStaticPropsContext } from "next";
import superjson from "superjson";
import { env } from "~/env";

import { prisma } from "~/server/db/client";
import { appRouter } from "~/server/trpc/root";
import { createTRPCContext } from "~/server/trpc/trpc";

export async function getStaticProps(context: GetStaticPropsContext<{ slug: string }>) {
  // create ssg helpers
  const ssg = createServerSideHelpers({
    router: appRouter,
    // skip ip, origin & host checks in SSR trpc pre-caching requests
    ctx: await createTRPCContext({ isSSR: true }),
    transformer: superjson, // optional - adds superjson serialization
  });
  const slug = context.params?.slug;
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const c = await ssg.card.getBySlug.fetch({ slug: slug! });
  // If the data is not found, redirect to a 404 page
  if (!c) {
    return { notFound: true };
  }
  return {
    props: {
      trpcState: ssg.dehydrate(),
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      slug: slug!,
    },
    revalidate: 60,
  };
}

export const getStaticPaths = (async () => {
  // don't prerender any static pages
  // (faster builds, but slower initial page load)
  if (env.VERCEL_ENV === "preview") {
    return {
      paths: [],
      fallback: "blocking",
    };
  }
  // Populate card slugs
  const cards = await prisma.card.findMany({
    select: {
      slug: true,
    },
  });

  // Get the paths we want to prerender based on our ids
  const paths = cards.map((card) => ({
    params: { slug: card.slug },
  }));

  return { paths, fallback: false };
}) satisfies GetStaticPaths;

export { default } from "./Card";
